import React from 'react';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AutoAwesomeIcon      from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon     from '@mui/icons-material/RocketLaunch';
import DiamondIcon          from '@mui/icons-material/Diamond';

// ─── Plan Limits ──────────────────────────────────────────────────────────────
export const PLAN_LIMITS = {
  free: {
    maxSurveys:   3,
    maxQuestions: 5,
    maxResponses: 1000,
    name: 'free',
  },
  professional: {
    maxSurveys:   15,
    maxQuestions: 10,
    maxResponses: 10_000,
    name: 'Pro',
  },
  premium: {
    maxSurveys:  30,
    maxQuestions: 20,
    maxResponses: 25_000,
    name: 'Premium',
  },
  enterprise: {
    maxSurveys:   50,
    maxQuestions: 25,
    maxResponses: 500_000,
    name: 'Enterprise',
  },
};

// ─── Stripe Price IDs ─────────────────────────────────────────────────────────
export const PRICE_IDS = {
  professional: 'price_1Teh62GELuwyK6kjeTFuUWTS',
  premium:      'price_1T3LwlGELuwyK6kjX4tckhwE',
  enterprise:   'price_1Tg6QSGELuwyK6kjdsnAF2MY',
};

// ─── Firestore collections ────────────────────────────────────────────────────
export const STRIPE_COLLECTIONS = {
  products:  'products',
  customers: 'users',
};

// ─── Redirect URLs ────────────────────────────────────────────────────────────
export const STRIPE_REDIRECT = {
  success: `${window.location.origin}/platform/suscripciones?success=true`,
  cancel:  `${window.location.origin}/platform/suscripciones?canceled=true`,
};

// ─── Number formatter ─────────────────────────────────────────────────────────
export const fmt = (n) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(0)}M`
    : n >= 1_000
    ? `${(n / 1_000).toFixed(0)}K`
    : `${n}`;

// ─── Discount price calculator ────────────────────────────────────────────────
// Recibe el precio base y el % de descuento (0-100).
// Devuelve el precio real (el que aparece en Stripe) redondeado a 2 decimales.
// Ejemplo: calcFinalPrice(4.9, 40) → 2.94
export const calcFinalPrice = (price, discount) => {
  if (!discount || discount <= 0 || !price) return price;
  const final = price * (1 - discount / 100);
  return Math.round(final * 100) / 100;
};

// ─── Translations ─────────────────────────────────────────────────────────────
export const TRANSLATIONS = {
  en: {
    // Page header
    pageTitle:    'Plans & Pricing',
    pageSubtitle: 'Billed monthly · Cancel anytime · No commitments',
    activePlan:   'Active plan',
    currency:     'USD / mo',

    // CTA buttons
    startFree:    'Start for free',
    currentPlan:  'Current plan',
    processing:   'Processing…',
    subscribeCta: (price) => `Subscribe — $${price}/mo`,

    // Discount badge & savings label
    discountBadge: (pct) => `${pct}% OFF`,
    savingsLabel:  (saved) => `You save $${saved}/mo`,

    // Confirm dialog
    confirmTitle: 'Confirm subscription',
    confirmBody:  (plan) => (
      <>
        You will be redirected to Stripe to complete payment for the{' '}
        <strong style={{ color: '#6366f1', textTransform: 'capitalize' }}>{plan}</strong> plan.
        Do you want to continue?
      </>
    ),
    cancelBtn:   'Cancel',
    goToPayment: 'Go to payment →',

    // Return banners
    successTitle: '🎉 Payment completed successfully!',
    successBody:  "Your subscription may take a few seconds to activate. If you don't see the change, reload the page.",
    cancelBanner: '↩️ Payment cancelled. You can subscribe whenever you like.',

    // Subscription details card
    detailsTitle:       'Subscription details',
    currentPeriodStart: 'Current period start',
    nextRenewal:        'Next renewal',
    accessUntil:        'Access until',
    daysLeft:           'Days remaining',
    expiresToday:       'Expires today',
    days:               (n) => `${n} day${n !== 1 ? 's' : ''}`,
    expiredDaysAgo:     (n) => `Expired ${n} day${n !== 1 ? 's' : ''} ago`,
    canceledWarning:    (date) =>
      `⚠️ Cancelled on ${date}. You keep access until the date shown above.`,

    // Portal card
    manageTitle: 'Manage subscription',
    manageBody:
      'Update your payment method, download invoices or cancel your plan from the secure Stripe portal.',
    openPortal: 'Open portal',

    // Stripe overlay
    redirectingPayment: 'Redirecting to Stripe…',
    preparingPayment:   'Preparing your secure payment session',
    redirectingPortal:  'Opening billing portal…',
    preparingPortal:    'Preparing your secure management portal',
    secureLabel:        '🔒 Encrypted connection · Powered by Stripe',

    // Footer
    footer: 'Payments securely processed by Stripe · Prices in USD',

    // Status badges
    status: {
      active:     'Active',
      trialing:   'Trialing',
      canceled:   'Cancelled',
      past_due:   'Past due',
      unpaid:     'Unpaid',
      incomplete: 'Incomplete',
    },

    // Plan feature lists
    features: {
      free: (l) => [
        `${l.maxSurveys} active surveys`,
        `${l.maxQuestions} questions per survey`,
        `${fmt(l.maxResponses)} responses / mo`,
        'Basic summary',
      ],
      professional: (l) => [
        `${l.maxSurveys} active surveys`,
        `${l.maxQuestions} questions per survey`,
        `${fmt(l.maxResponses)} responses / mo`,
        'Optimized charts & summary',
        'Receive comments on surveys',
        'Show your logo on surveys',
        'All emojis available',
        'Priority support',
      ],
      premium: (l) => [
        `${l.maxSurveys} active surveys`,
        `${l.maxQuestions} questions per survey`,
        `${fmt(l.maxResponses)} responses / mo`,
        'Optimized charts & summary',
        'Receive comments on surveys',
        'Show your logo on surveys',
        'All emojis available',
        '24 / 7 dedicated support',
      ],
      enterprise: (l) => [
        `${l.maxSurveys} active surveys`,
        `${l.maxQuestions} questions per survey`,
        `${fmt(l.maxResponses)} responses / mo`,
        'Optimized charts & summary',
        'Receive comments on surveys',
        'Show your logo on surveys',
        'All emojis available',
        '24 / 7 dedicated support',
      ],
    },
  },

  es: {
    pageTitle:    'Planes y Precios',
    pageSubtitle: 'Facturación mensual · Cancela cuando quieras · Sin compromisos',
    activePlan:   'Plan activo',
    currency:     'USD / mes',

    startFree:    'Comenzar gratis',
    currentPlan:  'Plan actual',
    processing:   'Procesando…',
    subscribeCta: (price) => `Suscribirse — $${price}/mes`,

    // Discount badge & savings label
    discountBadge: (pct) => `${pct}% OFF`,
    savingsLabel:  (saved) => `Ahorras $${saved}/mes`,

    confirmTitle: 'Confirmar suscripción',
    confirmBody:  (plan) => (
      <>
        Serás redirigido a Stripe para completar el pago del plan{' '}
        <strong style={{ color: '#6366f1', textTransform: 'capitalize' }}>{plan}</strong>.
        ¿Deseas continuar?
      </>
    ),
    cancelBtn:   'Cancelar',
    goToPayment: 'Ir a pago →',

    successTitle: '🎉 ¡Pago completado con éxito!',
    successBody:
      'Tu suscripción puede tardar unos segundos en activarse. Si no ves el cambio, recarga la página.',
    cancelBanner: '↩️ Pago cancelado. Puedes suscribirte cuando quieras.',

    detailsTitle:       'Detalles de suscripción',
    currentPeriodStart: 'Inicio del periodo actual',
    nextRenewal:        'Próxima renovación',
    accessUntil:        'Acceso hasta',
    daysLeft:           'Días restantes',
    expiresToday:       'Vence hoy',
    days:               (n) => `${n} día${n !== 1 ? 's' : ''}`,
    expiredDaysAgo:     (n) => `Venció hace ${n} día${n !== 1 ? 's' : ''}`,
    canceledWarning:    (date) =>
      `⚠️ Cancelada el ${date}. Conservas acceso hasta la fecha límite indicada.`,

    manageTitle: 'Gestionar suscripción',
    manageBody:
      'Actualiza tu método de pago, descarga facturas o cancela tu plan desde el portal seguro de Stripe.',
    openPortal: 'Abrir portal',

    redirectingPayment: 'Redirigiendo a Stripe…',
    preparingPayment:   'Preparando tu sesión de pago seguro',
    redirectingPortal:  'Abriendo portal de facturación…',
    preparingPortal:    'Preparando tu portal de gestión seguro',
    secureLabel:        '🔒 Conexión cifrada · Procesado por Stripe',

    footer: 'Pagos procesados de forma segura por Stripe · Precios en USD',

    status: {
      active:     'Activa',
      trialing:   'En prueba',
      canceled:   'Cancelada',
      past_due:   'Pago vencido',
      unpaid:     'Sin pagar',
      incomplete: 'Incompleta',
    },

    features: {
      free: (l) => [
        `${l.maxSurveys} encuestas activas`,
        `${l.maxQuestions} preguntas por encuesta`,
        `${fmt(l.maxResponses)} respuestas / mes`,
        'Resumen básico',
      ],
      professional: (l) => [
        `${l.maxSurveys} encuestas activas`,
        `${l.maxQuestions} preguntas por encuesta`,
        `${fmt(l.maxResponses)} respuestas / mes`,
        'Gráficos y resumen optimizado',
        'Recibe comentarios en tus encuestas',
        'Muestra tu logo en las encuestas',
        'Todos los emojis disponibles',
        'Soporte prioritario',
      ],
      premium: (l) => [
        `${l.maxSurveys} encuestas activas`,
        `${l.maxQuestions} preguntas por encuesta`,
        `${fmt(l.maxResponses)} respuestas / mes`,
        'Gráficos y resumen optimizado',
        'Recibe comentarios en tus encuestas',
        'Muestra tu logo en las encuestas',
        'Todos los emojis disponibles',
        'Soporte 24 / 7 dedicado',
      ],
      enterprise: (l) => [
        `${l.maxSurveys} encuestas activas`,
        `${l.maxQuestions} preguntas por encuesta`,
        `${fmt(l.maxResponses)} respuestas / mes`,
        'Gráficos y resumen optimizado',
        'Recibe comentarios en tus encuestas',
        'Muestra tu logo en las encuestas',
        'Todos los emojis disponibles',
        'Soporte 24 / 7 dedicado',
      ],
    },
  },
};

// ─── Helper: resolve translations for a given lang (defaults to 'en') ─────────
export const getT = (lang) => TRANSLATIONS[lang] ?? TRANSLATIONS.en;

// ─── Plan definitions ──────────────────────────────────────────────────────────
// `discount` es opcional (0-100). Cuando está definido:
//   - Se muestra el precio base tachado (precio "de lista")
//   - El precio real = calcFinalPrice(price, discount) → es el que aparece en Stripe
//   - Se muestra un badge "X% OFF"
// Para quitar el descuento de un plan, borra el campo `discount` o ponlo en 0.
export const PLANS = [
  {
    key:   'free',
    icon:  <RocketLaunchIcon sx={{ fontSize: 26 }} />,
    label: 'Free',
    price: 0,
    // sin descuento
  },
  {
    key:      'professional',
    icon:     <AutoAwesomeIcon sx={{ fontSize: 26 }} />,
    label:    'Pro',
    price:    9.07,
    discount: 45,   // ← precio real en Stripe = calcFinalPrice(4.9, 40) = $2.94
  },
  {
    key:   'premium',
    icon:  <WorkspacePremiumIcon sx={{ fontSize: 26 }} />,
    label: 'Premium',
    price: 25,
    discount: 40, 
    // sin descuento — agrega discount: XX para activarlo
  },
  {
    key:      'enterprise',
    icon:     <DiamondIcon sx={{ fontSize: 26 }} />,
    label:    'Enterprise',
    price:    29,
      // ← precio real en Stripe = calcFinalPrice(29, 20) = $23.2
  },
];